import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual, isNil } from 'lodash';

import {
  getCustomerPayrollCycleLabel,
  getCustomerInvoicingCycleLabel,
  getCustomerCostingMethodTypeLabel,
  getCommissionTypeApprovalLabel,
  getCustomerPaymentTermsLabel,
  getPaymentMethodTypeLabel,
} from 'utils/clientUtils';
import {
  CustomerDocumentTypeEnum,
  CustomerHistoryDetailsModel,
  FullCustomerInfoResponseModel,
} from 'api/adminApiClient';
import BaseAlert from 'components/wrappers/BaseAlert';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getCustomerHistoryFile } from 'redux/reducers/clientSlice';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import { getYesNoAnswer } from 'components/ActivitiesDetailsSection/HistorySection/utils';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';
import styles from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryCard.module.less';

type Props = {
  customerId: string;
  historyDetails: CustomerHistoryDetailsModel;
  historyId: string;
};

export default function FinancialInfoDetailsCard(props: Props) {
  const { historyDetails, customerId, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();
  // Rate Overview
  const hasRateOverviewChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.rateOverview,
    newData?.additionalInfo?.financialInformation?.rateOverview,
  );
  const hasFixedMarkupRateChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.rateOverview
      ?.fixedMarkupRate,
    newData?.additionalInfo?.financialInformation?.rateOverview
      ?.fixedMarkupRate,
  );
  const hasDTBillRateMultiplierChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.rateOverview
      ?.dtBillRateMultiplier,
    newData?.additionalInfo?.financialInformation?.rateOverview
      ?.dtBillRateMultiplier,
  );
  const hasOTRateMultiplierChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.rateOverview
      ?.otRateMultiplier,
    newData?.additionalInfo?.financialInformation?.rateOverview
      ?.otRateMultiplier,
  );

  /// MSP/VMS overview
  const hasMspVmsOverviewChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview,
  );
  const hasMspFeeChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.mspFee,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.mspFee,
  );
  const hasVmsFeeChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.vmsFee,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.vmsFee,
  );
  const hasInvoicingInformationChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.invoicingInformation,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.invoicingInformation,
  );
  const hasSpecialInvoicingInstructionsChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.specialInvoicingInstructions,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.specialInvoicingInstructions,
  );
  const hasInvoicingCycleChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.invoicingCycle,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.invoicingCycle,
  );
  const hasPayrollCycleChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.payrollCycle,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.payrollCycle,
  );
  const hasIsThereMspOrVmsChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.isThereMspOrVms,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.isThereMspOrVms,
  );
  const hasURLChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.url,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.url,
  );
  const hasMspVmsProviderNameChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.mspOrVmsProviderName,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.mspOrVmsProviderName,
  );
  const hasPurchaseOrderChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.purchaseOrder,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.purchaseOrder,
  );
  const hasPurchaseOrderInstructionsChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.purchaseOrderInstructions,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.purchaseOrderInstructions,
  );
  const hasPurchaseOrderFileKeyChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.purchaseOrderFileKey,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.purchaseOrderFileKey,
  );
  const hasContractorConversionFeesChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.contractorConversionFees,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.contractorConversionFees,
  );
  const hasCustomerCreditNotesChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.customerCreditNotes,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.customerCreditNotes,
  );
  const hasDunsNumberChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview?.dunsNumber,
    newData?.additionalInfo?.financialInformation?.financialOverview?.dunsNumber,
  );
  const hasInvoiceCutOffPeriodChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.invoiceCutOffPeriod,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.invoiceCutOffPeriod,
  );
  const hasInvoiceDisputePeriodChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.invoiceDisputePeriod,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.invoiceDisputePeriod,
  );
  const hasPaymentMethodChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.paymentMethod,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.paymentMethod,
  );
  const hasPayorNameChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview?.payorName,
    newData?.additionalInfo?.financialInformation?.financialOverview?.payorName,
  );
  // Financial overview
  const hasFinancialOverviewChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview,
    newData?.additionalInfo?.financialInformation?.financialOverview,
  );
  const hasCreditCheckScoreChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.creditCheckScore,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.creditCheckScore,
  );
  const hasCommissionTypeApprovalChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.fundingType,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.fundingType,
  );
  const hasAddedNotesChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.financialSpecialInstructions,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.financialSpecialInstructions,
  );
  const hasPaymentTermsChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.paymentTerm,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.paymentTerm,
  );
  const hasCustomerCostingMethodTypeChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.customerCostingMethodType,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.customerCostingMethodType,
  );

  const handleFileDownload = async (
    fileKey: string,
    documentType: CustomerDocumentTypeEnum,
  ) => {
    const result = await dispatch(
      getCustomerHistoryFile({
        customerId,
        fileKey,
        documentType,
      }),
    );

    if (getCustomerHistoryFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const renderRateOverviewSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'rate-overview';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Rate overview</div>
          </Col>

          {hasDTBillRateMultiplierChanged && (
            <HistoryField
              title="DT bill rate multiplier"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.rateOverview?.dtBillRateMultiplier,
                )
                  ? String(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.rateOverview?.dtBillRateMultiplier,
                    )
                  : undefined
              }
              dataId={`${sectionName}-DT-bill-rate-multiplier-field`}
            />
          )}

          {hasFixedMarkupRateChanged && (
            <HistoryField
              title="Fixed markup rate"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.rateOverview?.fixedMarkupRate
              }
              dataId={`${sectionName}-markup-rate-field`}
            />
          )}

          {hasOTRateMultiplierChanged && (
            <HistoryField
              title="OT Rate multiplier"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.rateOverview?.otRateMultiplier
              }
              dataId={`${sectionName}-ot-rate-multiplier-field`}
            />
          )}

        </Row>

        <Divider />
      </>
    );
  };

  const renderMspVmsOverviewSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'MSP-VMS-overview';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>MSP/VMS overview</div>
          </Col>
          {hasMspFeeChanged && (
            <HistoryField
              title="MSP fee"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.mspOrVmsOverview?.mspFee,
                )
                  ? `${customerDetails?.additionalInfo?.financialInformation?.mspOrVmsOverview?.mspFee?.toFixed(
                      2,
                    )}%`
                  : undefined
              }
              dataId={`${sectionName}-MSP-fee-field`}
            />
          )}
          {hasVmsFeeChanged && (
            <HistoryField
              title="VMS fee"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.mspOrVmsOverview?.vmsFee,
                )
                  ? `${customerDetails?.additionalInfo?.financialInformation?.mspOrVmsOverview?.vmsFee?.toFixed(
                      2,
                    )}%`
                  : undefined
              }
              dataId={`${sectionName}-VMS-fee-field`}
            />
          )}
          {hasIsThereMspOrVmsChanged && (
            <HistoryField
              title="Is there an MSP or VMS?"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.isThereMspOrVms,
              )}
              dataId={`${sectionName}-is-there-msp-or-vms-field`}
            />
          )}
          {hasURLChanged && (
            <HistoryField
              title="URL"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.url
              }
              dataId={`${sectionName}-URL-field`}
            />
          )}
          {hasMspVmsProviderNameChanged && (
            <HistoryField
              title="MSP/VMS provider name"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.mspOrVmsProviderName
              }
              dataId={`${sectionName}-provider-name-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderFinancialOverviewSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'financial-overview';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Financial overview</div>
          </Col>

          {hasCreditCheckScoreChanged && (
            <HistoryField
              title="Credit check score"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.creditCheckScore
              }
              dataId={`${sectionName}-credit-check-score-field`}
            />
          )}

          {hasCommissionTypeApprovalChanged && (
            <HistoryField
              title="Funding type"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.financialOverview?.fundingType,
                )
                  ? getCommissionTypeApprovalLabel(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.financialOverview?.fundingType,
                    )
                  : undefined
              }
              dataId={`${sectionName}-commission-type-approval-field`}
            />
          )}

          {hasAddedNotesChanged && (
            <HistoryField
              title="Financial special instructions"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.financialSpecialInstructions
              }
              dataId={`${sectionName}-added-notes-field`}
            />
          )}

          {hasPaymentTermsChanged && (
            <HistoryField
              title="Payment terms"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.financialOverview?.paymentTerm,
                )
                  ? getCustomerPaymentTermsLabel(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.financialOverview?.paymentTerm,
                    )
                  : undefined
              }
              dataId={`${sectionName}-payment-terms-field`}
            />
          )}

          {hasCustomerCostingMethodTypeChanged && (
            <HistoryField
              title="Customer costing method type"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.financialOverview?.customerCostingMethodType,
                )
                  ? getCustomerCostingMethodTypeLabel(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.financialOverview?.customerCostingMethodType,
                    )
                  : undefined
              }
              dataId={`${sectionName}-customer-costing-method-type-field`}
            />
          )}

          {hasContractorConversionFeesChanged && (
            <HistoryField
              title="Contractor conversion fees?"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.contractorConversionFees,
              )}
              dataId={`${sectionName}-contractor-conversion-fees-field`}
            />
          )}

          {hasCustomerCreditNotesChanged && (
            <HistoryField
              title="Customer credit notes"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.customerCreditNotes
              }
              dataId={`${sectionName}-customer-credit-notes-field`}
            />
          )}

          {hasDunsNumberChanged && (
            <HistoryField
              title="D-U-N-S number"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.dunsNumber
              }
              dataId={`${sectionName}-duns-number-field`}
            />
          )}

          {hasInvoicingCycleChanged && (
            <HistoryField
              title="Invoicing cycle"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.financialOverview?.invoicingCycle,
                )
                  ? getCustomerInvoicingCycleLabel(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.financialOverview?.invoicingCycle,
                    )
                  : undefined
              }
              dataId={`${sectionName}-invoicing-cycle-field`}
            />
          )}

          {hasInvoicingInformationChanged && (
            <HistoryField
              title="Invoicing information"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.invoicingInformation
              }
              dataId={`${sectionName}-invoicing-information-field`}
            />
          )}

          {hasSpecialInvoicingInstructionsChanged && (
            <HistoryField
              title="Special invoicing instructions"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.specialInvoicingInstructions
              }
              dataId={`${sectionName}-special-invoicing-instructions-field`}
            />
          )}

          {hasPayrollCycleChanged && (
            <HistoryField
              title="Pay period"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.financialOverview?.payrollCycle,
                )
                  ? getCustomerPayrollCycleLabel(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.financialOverview?.payrollCycle,
                    )
                  : undefined
              }
              dataId={`${sectionName}-payroll-cycle-field`}
            />
          )}

          {hasPurchaseOrderChanged && (
            <HistoryField
              title="Purchase order"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.purchaseOrder,
              )}
              dataId={`${sectionName}-purchase-order-field`}
            />
          )}

          {hasPurchaseOrderInstructionsChanged && (
            <HistoryField
              title="Purchase order instructions"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.purchaseOrderInstructions
              }
              dataId={`${sectionName}-purchase-order-instructions-field`}
            />
          )}

          {hasPurchaseOrderFileKeyChanged && (
            <HistoryField
              title="Purchase order"
              emptyText="No file"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.purchaseOrderFileKey &&
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.purchaseOrderFileName && (
                  <span
                    className="link-imitation"
                    onClick={() =>
                      handleFileDownload(
                        customerDetails?.additionalInfo?.financialInformation
                          ?.financialOverview?.purchaseOrderFileKey as string,
                        CustomerDocumentTypeEnum.PurchaseOrder,
                      )
                    }
                  >
                    {getFileNameWithoutExt(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.financialOverview?.purchaseOrderFileName || '',
                    )}
                  </span>
                )
              }
              dataId={`${sectionName}-purchase-order-file-field`}
            />
          )}

          {hasInvoiceCutOffPeriodChanged && (
            <HistoryField
              title="Invoice cut off period"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.invoiceCutOffPeriod
              }
              dataId={`${sectionName}-invoice-cut-off-field`}
            />
          )}

          {hasInvoiceDisputePeriodChanged && (
            <HistoryField
              title="Invoice dispute period"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.invoiceDisputePeriod
              }
              dataId={`${sectionName}-invoice-dispute-field`}
            />
          )}

          {hasPaymentMethodChanged && (
            <HistoryField
              title="Payment method"
              value={getPaymentMethodTypeLabel(
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.paymentMethod,
              )}
              dataId={`${sectionName}-payment-method-field`}
            />
          )}

          {hasPayorNameChanged && (
            <HistoryField
              title="Payor name"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.payorName
              }
              dataId={`${sectionName}-payor-name-field`}
            />
          )}
        </Row>
        <Divider />
      </>
    );
  };

  const renderAdditionalInformation = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    return (
      <>
        {hasFinancialOverviewChanged &&
          renderFinancialOverviewSection(customerDetails)}

        {hasRateOverviewChanged && renderRateOverviewSection(customerDetails)}

        {hasMspVmsOverviewChanged &&
          renderMspVmsOverviewSection(customerDetails)}
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderAdditionalInformation(oldData)}
      newData={renderAdditionalInformation(newData)}
    />
  );
}
